import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import type {
  AbandonCampaignInput,
  AbandonCampaignResultDto,
  AddClientScopeInput,
  AddCycleInput,
  AddParticipationInput,
  AddProfileScopeInput,
  AddProfileScopeBatchInput,
  CampaignClient,
  CampaignDetailDto,
  CampaignListItemDto,
  CatalogTreeNodeDto,
  ClientScopeDto,
  CloneCampaignInput,
  CloseCampaignResultDto,
  CloseCycleInput,
  CycleNumberDto,
  CloseCycleResultDto,
  CreateCampaignInput,
  CycleScheduleDto,
  OpenCampaignC2ResultDto,
  OpenCampaignResultDto,
  ProfileScopeDto,
  SetCampaignDatesInput,
  SetTargetPriceConfigInput,
  SupplierParticipationDto,
  ApplyTargetPriceDefaultsResultDto,
  TargetPriceConfigDto,
  TargetPriceDefaultsDto,
} from '@/api/campaign-client';
import { CampaignApiError } from '@/api/campaign-client';
import { useCampaignClient } from '@/api/campaign-client-context';

// =============================================================================
// use-campaigns.ts — Hooks TanStack Query du BC campaign ADMIN (S10).
// =============================================================================
// Lectures (liste, détail) en useQuery ; commandes (création, scope, planning,
// clonage) en useMutation (effets de bord serveur). Le client effectif vient du
// CampaignClientProvider (doublable, stub-first).
//
// ZÉRO règle métier ici : l'autorité (RBAC AdminSessionGuard, withTenant,
// estampillage catalog_version_id, refus figement 409, validité des cycles) est
// l'API. Les hooks relaient l'intention et, après une mutation, invalident les
// queries impactées pour refléter l'état serveur. L'erreur API (dont
// CampaignApiError kind 'no-published-catalog') remonte telle quelle.

const CAMPAIGNS_KEY = ['campaigns'] as const;

/** Clé de query de la liste des campagnes. */
export const campaignsListKey = [...CAMPAIGNS_KEY, 'list'] as const;
/** Clé de query du détail d'une campagne. */
export function campaignDetailKey(campaignId: string): readonly unknown[] {
  return [...CAMPAIGNS_KEY, 'detail', campaignId];
}

/** Clé de query des configs de Prix Cible d'une campagne (S11). */
export function targetPricesKey(campaignId: string): readonly unknown[] {
  return [...CAMPAIGNS_KEY, 'target-prices', campaignId];
}

/**
 * Clé de query des valeurs proposées (L10) : SOUS le préfixe `targetPricesKey`, donc invalidée avec lui
 * (toute pose de prix rafraîchit aussi la proposition et les drapeaux `locked`).
 */
export function targetPriceDefaultsKey(campaignId: string): readonly unknown[] {
  return [...targetPricesKey(campaignId), 'defaults'];
}

/** Clé de query de l'arbre du périmètre métier (V-CREATE, PUBLISHED courante). */
export const catalogTreeKey = [...CAMPAIGNS_KEY, 'catalog-tree'] as const;

/**
 * V-CREATE (DBU §2.5 l.1131) — arbre du périmètre métier (familles → sous-familles → profils) de la
 * version PUBLISHED courante, pour le sélecteur de périmètre. Vide si aucune version publiée.
 */
export function useCatalogTree(): UseQueryResult<
  readonly CatalogTreeNodeDto[],
  Error
> {
  const client: CampaignClient = useCampaignClient();
  return useQuery<readonly CatalogTreeNodeDto[], Error>({
    queryKey: catalogTreeKey,
    queryFn: () => client.listCatalogTree(),
  });
}

/** GET liste des campagnes. */
export function useCampaignsList(): UseQueryResult<
  readonly CampaignListItemDto[],
  Error
> {
  const client: CampaignClient = useCampaignClient();
  return useQuery<readonly CampaignListItemDto[], Error>({
    queryKey: campaignsListKey,
    queryFn: () => client.listCampaigns(),
  });
}

/**
 * GET détail d'une campagne (campagne + scopes + participations + cycles). `options.enabled`
 * (défaut `true`, rétro-compatible) permet de GATER la lecture — ex. filtre « campagne » du registre
 * T2 : on ne résout le détail que lorsqu'une campagne est effectivement sélectionnée.
 */
export function useCampaignDetail(
  campaignId: string,
  options: { readonly enabled?: boolean } = {},
): UseQueryResult<CampaignDetailDto, Error> {
  const client: CampaignClient = useCampaignClient();
  return useQuery<CampaignDetailDto, Error>({
    queryKey: campaignDetailKey(campaignId),
    queryFn: () => client.getCampaign(campaignId),
    enabled: options.enabled ?? true,
  });
}

/** Création d'une campagne (intitulé SEUL). 409 figement remonte tel quel. */
export function useCreateCampaign(): UseMutationResult<
  CampaignListItemDto,
  Error,
  CreateCampaignInput
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<CampaignListItemDto, Error, CreateCampaignInput>({
    mutationKey: [...CAMPAIGNS_KEY, 'create'],
    mutationFn: (input) => client.createCampaign(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}

/** Argument générique d'une mutation ciblant une campagne avec corps. */
interface CampaignWith<TBody> {
  readonly campaignId: string;
  readonly body: TBody;
}

/** Clonage d'une campagne (gabarit) → nouvelle campagne Brouillon. */
export function useCloneCampaign(): UseMutationResult<
  CampaignListItemDto,
  Error,
  CampaignWith<CloneCampaignInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<
    CampaignListItemDto,
    Error,
    CampaignWith<CloneCampaignInput>
  >({
    mutationKey: [...CAMPAIGNS_KEY, 'clone'],
    mutationFn: ({ campaignId, body }) =>
      client.cloneCampaign(campaignId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}

/** Ajout d'un client au périmètre. */
export function useAddClientScope(): UseMutationResult<
  ClientScopeDto,
  Error,
  CampaignWith<AddClientScopeInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<ClientScopeDto, Error, CampaignWith<AddClientScopeInput>>({
    mutationKey: [...CAMPAIGNS_KEY, 'add-client-scope'],
    mutationFn: ({ campaignId, body }) =>
      client.addClientScope(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
    },
  });
}

/** Argument d'une mutation de retrait (campagne + identifiant de l'entrée). */
interface RemoveArg {
  readonly campaignId: string;
  readonly entryId: string;
}

/** Retrait d'un client du périmètre. */
export function useRemoveClientScope(): UseMutationResult<
  void,
  Error,
  RemoveArg
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<void, Error, RemoveArg>({
    mutationKey: [...CAMPAIGNS_KEY, 'remove-client-scope'],
    mutationFn: ({ campaignId, entryId }) =>
      client.removeClientScope(campaignId, entryId),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
    },
  });
}

/** Argument de la mutation B4 : campagne + valeur cible du flag de diffusion. */
interface SetClientsDiffusionArg {
  readonly campaignId: string;
  readonly enabled: boolean;
}

/**
 * B4 (relevé GATE_HUMAN 2026-09-15) — AUTORISE / révoque l'affichage des noms de clients bénéficiaires.
 * Au succès, invalide le détail (le `clientsDiffusionEnabled` re-lu reflète l'état serveur). 404/400
 * remontent tels quels (autorité API).
 */
export function useSetClientsDiffusion(): UseMutationResult<
  void,
  Error,
  SetClientsDiffusionArg
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<void, Error, SetClientsDiffusionArg>({
    mutationKey: [...CAMPAIGNS_KEY, 'set-clients-diffusion'],
    mutationFn: ({ campaignId, enabled }) =>
      client.setClientsDiffusion(campaignId, enabled),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
    },
  });
}

/** Ajout d'un profil + niveau au périmètre. */
export function useAddProfileScope(): UseMutationResult<
  ProfileScopeDto,
  Error,
  CampaignWith<AddProfileScopeInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<
    ProfileScopeDto,
    Error,
    CampaignWith<AddProfileScopeInput>
  >({
    mutationKey: [...CAMPAIGNS_KEY, 'add-profile-scope'],
    mutationFn: ({ campaignId, body }) =>
      client.addProfileScope(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      // Le périmètre / l'état conditionnent les valeurs proposées et le verrou des prix cibles (L10).
      void qc.invalidateQueries({ queryKey: targetPricesKey(campaignId) });
    },
  });
}

/**
 * Ajout EN LOT de profils au périmètre (V-CREATE, inclusion AUTO des niveaux). Au succès, invalide le
 * détail (le périmètre profil re-lu reflète tous les couples posés).
 */
export function useAddProfileScopeBatch(): UseMutationResult<
  readonly ProfileScopeDto[],
  Error,
  CampaignWith<AddProfileScopeBatchInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<
    readonly ProfileScopeDto[],
    Error,
    CampaignWith<AddProfileScopeBatchInput>
  >({
    mutationKey: [...CAMPAIGNS_KEY, 'add-profile-scope-batch'],
    mutationFn: ({ campaignId, body }) =>
      client.addProfileScopeBatch(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      // Le périmètre / l'état conditionnent les valeurs proposées et le verrou des prix cibles (L10).
      void qc.invalidateQueries({ queryKey: targetPricesKey(campaignId) });
    },
  });
}

/** Retrait d'un profil du périmètre. */
export function useRemoveProfileScope(): UseMutationResult<
  void,
  Error,
  RemoveArg
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<void, Error, RemoveArg>({
    mutationKey: [...CAMPAIGNS_KEY, 'remove-profile-scope'],
    mutationFn: ({ campaignId, entryId }) =>
      client.removeProfileScope(campaignId, entryId),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      // Le périmètre / l'état conditionnent les valeurs proposées et le verrou des prix cibles (L10).
      void qc.invalidateQueries({ queryKey: targetPricesKey(campaignId) });
    },
  });
}

/** Ajout d'une participation fournisseur. */
export function useAddParticipation(): UseMutationResult<
  SupplierParticipationDto,
  Error,
  CampaignWith<AddParticipationInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<
    SupplierParticipationDto,
    Error,
    CampaignWith<AddParticipationInput>
  >({
    mutationKey: [...CAMPAIGNS_KEY, 'add-participation'],
    mutationFn: ({ campaignId, body }) =>
      client.addParticipation(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
    },
  });
}

/** Retrait d'une participation fournisseur. */
export function useRemoveParticipation(): UseMutationResult<
  void,
  Error,
  RemoveArg
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<void, Error, RemoveArg>({
    mutationKey: [...CAMPAIGNS_KEY, 'remove-participation'],
    mutationFn: ({ campaignId, entryId }) =>
      client.removeParticipation(campaignId, entryId),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
    },
  });
}

/** Planification d'un cycle (C1 ou C2). */
export function useAddCycle(): UseMutationResult<
  CycleScheduleDto,
  Error,
  CampaignWith<AddCycleInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<CycleScheduleDto, Error, CampaignWith<AddCycleInput>>({
    mutationKey: [...CAMPAIGNS_KEY, 'add-cycle'],
    mutationFn: ({ campaignId, body }) => client.addCycle(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
    },
  });
}

/** Argument de suppression d'un cycle : campagne + numéro de cycle (1|2). */
interface RemoveCycleArg {
  readonly campaignId: string;
  readonly cycleNumber: CycleNumberDto;
}

/**
 * Suppression d'un cycle planifié (GATE_HUMAN 2026-09-09 : correction d'une saisie erronée). L'API refuse
 * (409 `CAMPAIGN_CYCLE_NOT_PENDING`) un cycle déjà ouvert/clôturé. Au succès, invalide le détail.
 */
export function useRemoveCycle(): UseMutationResult<void, Error, RemoveCycleArg> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<void, Error, RemoveCycleArg>({
    mutationKey: [...CAMPAIGNS_KEY, 'remove-cycle'],
    mutationFn: ({ campaignId, cycleNumber }) =>
      client.removeCycle(campaignId, cycleNumber),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
    },
  });
}

/**
 * V-CREATE (DBU §2.5 l.1125) — pose les dates GLOBALES de démarrage/fin de la campagne (F0-d). 422 si
 * incohérent (autorité API). Au succès, invalide le détail + la liste (dates reflétées).
 */
export function useSetCampaignDates(): UseMutationResult<
  CampaignListItemDto,
  Error,
  CampaignWith<SetCampaignDatesInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<
    CampaignListItemDto,
    Error,
    CampaignWith<SetCampaignDatesInput>
  >({
    mutationKey: [...CAMPAIGNS_KEY, 'set-dates'],
    mutationFn: ({ campaignId, body }) =>
      client.setCampaignDates(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}

// --- Prix Cibles & marges (S11, UC-A-044) ------------------------------------

/** GET des configs de Prix Cible d'une campagne. */
export function useTargetPriceConfigs(
  campaignId: string,
): UseQueryResult<readonly TargetPriceConfigDto[], Error> {
  const client: CampaignClient = useCampaignClient();
  return useQuery<readonly TargetPriceConfigDto[], Error>({
    queryKey: targetPricesKey(campaignId),
    queryFn: () => client.listTargetPriceConfigs(campaignId),
  });
}

/** Pose (upsert) la config de Prix Cible d'un couple (profil, séniorité). */
export function useSetTargetPriceConfig(): UseMutationResult<
  TargetPriceConfigDto,
  Error,
  CampaignWith<SetTargetPriceConfigInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<
    TargetPriceConfigDto,
    Error,
    CampaignWith<SetTargetPriceConfigInput>
  >({
    mutationKey: [...CAMPAIGNS_KEY, 'set-target-price'],
    mutationFn: ({ campaignId, body }) =>
      client.setTargetPriceConfig(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: targetPricesKey(campaignId) });
    },
    onError: (error, { campaignId }) => {
      refreshOnTargetPriceConflict(qc, error, campaignId);
    },
  });
}

/**
 * L10 : un refus 409 (valeur Pulse changée, campagne figée) rend la proposition affichée obsolète ⇒ relecture
 * des prix et des valeurs proposées. BUSY (clôture concurrente) relit aussi : l'état peut avoir basculé.
 */
function refreshOnTargetPriceConflict(
  qc: ReturnType<typeof useQueryClient>,
  error: Error,
  campaignId: string,
): void {
  if (
    error instanceof CampaignApiError &&
    (error.kind === 'target-price-default-mismatch' ||
      error.kind === 'target-price-locked' ||
      error.kind === 'target-price-busy')
  ) {
    void qc.invalidateQueries({ queryKey: targetPricesKey(campaignId) });
  }
}

/**
 * L10 (UC-A-044) — valeurs proposées (P50 Pulse) par couple du périmètre + drapeaux `pulseDefaultEnabled` et
 * `locked` (GET /admin/campaigns/:id/target-prices/defaults).
 */
export function useTargetPriceDefaults(
  campaignId: string,
): UseQueryResult<TargetPriceDefaultsDto, Error> {
  const client: CampaignClient = useCampaignClient();
  return useQuery<TargetPriceDefaultsDto, Error>({
    queryKey: targetPriceDefaultsKey(campaignId),
    queryFn: () => client.listTargetPriceDefaults(campaignId),
  });
}

/**
 * L10 (UC-A-044, A1) — « Appliquer les valeurs Pulse » : pose la valeur proposée sur les seuls couples SANS
 * prix (jamais d'écrasement, autorité API). Relit prix et propositions au succès comme au refus 409.
 */
export function useApplyTargetPriceDefaults(): UseMutationResult<
  ApplyTargetPriceDefaultsResultDto,
  Error,
  { readonly campaignId: string }
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<
    ApplyTargetPriceDefaultsResultDto,
    Error,
    { readonly campaignId: string }
  >({
    mutationKey: [...CAMPAIGNS_KEY, 'apply-target-price-defaults'],
    mutationFn: ({ campaignId }) => client.applyTargetPriceDefaults(campaignId),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: targetPricesKey(campaignId) });
    },
    onError: (error, { campaignId }) => {
      refreshOnTargetPriceConflict(qc, error, campaignId);
    },
  });
}

// --- Planification (« Planifier le paramétrage », S11, E-3b-1) ---------------

/**
 * Planifie une campagne (Brouillon → Planifiee). 409 si paramétrage incomplet
 * (CampaignApiError kind 'not-ready-to-plan') ou état invalide ('plan-wrong-state').
 * Au succès, invalide détail + liste pour refléter le nouvel état serveur.
 */
export function usePlanCampaign(): UseMutationResult<
  CampaignListItemDto,
  Error,
  string
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<CampaignListItemDto, Error, string>({
    mutationKey: [...CAMPAIGNS_KEY, 'plan'],
    mutationFn: (campaignId) => client.planCampaign(campaignId),
    onSuccess: (_data, campaignId) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}

// --- Ouverture (« Lancement », S11, UC-A-046) --------------------------------

/** Ouvre une campagne (Planifiee → OuverteC1). 409 si périmètre incomplet / phase invalide. */
export function useOpenCampaign(): UseMutationResult<
  OpenCampaignResultDto,
  Error,
  string
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<OpenCampaignResultDto, Error, string>({
    mutationKey: [...CAMPAIGNS_KEY, 'open'],
    mutationFn: (campaignId) => client.openCampaign(campaignId),
    onSuccess: (_data, campaignId) => {
      // L'ouverture change l'état de la campagne ET du cycle C1 : invalide détail + liste.
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}

// --- Pilotage des clôtures / ouverture C2 / abandon (S15) --------------------

/**
 * CLÔTURE anticipée (EARLY-CLOSE) d'un cycle (UC-A-049 reframé). 409 si état/cycle invalides
 * ('close-wrong-state' / 'close-unknown-cycle'). Au succès, invalide détail + liste : l'état bascule
 * en analyse (AnalyseC1/AnalyseC2) et les submissions gelées (worker) sont re-lues.
 */
export function useCloseCycle(): UseMutationResult<
  CloseCycleResultDto,
  Error,
  CampaignWith<CloseCycleInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<CloseCycleResultDto, Error, CampaignWith<CloseCycleInput>>({
    mutationKey: [...CAMPAIGNS_KEY, 'close-cycle'],
    mutationFn: ({ campaignId, body }) => client.closeCycle(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}

/**
 * OUVERTURE du Cycle 2 (UC-A-050). CONDITIONNÉE à l'état AnalyseC1 côté API ; 409 si conditions non
 * réunies ('open-c2-wrong-state' / '_empty-*' / '_no-validated-target-price' / '_no-planned-cycle').
 * Au succès, invalide détail + liste (état → OuverteC2, cycle C2 → Ouvert).
 */
export function useOpenCampaignC2(): UseMutationResult<
  OpenCampaignC2ResultDto,
  Error,
  string
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<OpenCampaignC2ResultDto, Error, string>({
    mutationKey: [...CAMPAIGNS_KEY, 'open-c2'],
    mutationFn: (campaignId) => client.openCampaignC2(campaignId),
    onSuccess: (_data, campaignId) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}

/**
 * ABANDON IRRÉVERSIBLE d'une campagne (UC-A-053) → Abandonnee (terminal). 409
 * 'abandon-already-terminal' si déjà terminal. Au succès, invalide détail + liste (état terminal).
 */
export function useAbandonCampaign(): UseMutationResult<
  AbandonCampaignResultDto,
  Error,
  CampaignWith<AbandonCampaignInput>
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<
    AbandonCampaignResultDto,
    Error,
    CampaignWith<AbandonCampaignInput>
  >({
    mutationKey: [...CAMPAIGNS_KEY, 'abandon'],
    mutationFn: ({ campaignId, body }) =>
      client.abandonCampaign(campaignId, body),
    onSuccess: (_data, { campaignId }) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      // Le périmètre / l'état conditionnent les valeurs proposées et le verrou des prix cibles (L10).
      void qc.invalidateQueries({ queryKey: targetPricesKey(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}

/**
 * CLÔTURE DÉFINITIVE d'une campagne (UC-A-052, ARBITRAGE 1 S17) : émet l'UNIQUE trigger du calcul
 * consolidé (DE.CampaignClosed) depuis l'état d'analyse final, SANS transiter l'état (frontière gelée).
 * Action TERMINALE et IRRÉVERSIBLE, IDEMPOTENTE (re-commande = NO-OP `alreadyClosed:true`). 409
 * 'final-close-wrong-state' si l'état n'est pas un état d'analyse final. Au succès, invalide détail +
 * liste (marqueur de clôture / consolidation en cours re-lu).
 */
export function useCloseCampaign(): UseMutationResult<
  CloseCampaignResultDto,
  Error,
  string
> {
  const client: CampaignClient = useCampaignClient();
  const qc = useQueryClient();
  return useMutation<CloseCampaignResultDto, Error, string>({
    mutationKey: [...CAMPAIGNS_KEY, 'close'],
    mutationFn: (campaignId) => client.closeCampaign(campaignId),
    onSuccess: (_data, campaignId) => {
      void qc.invalidateQueries({ queryKey: campaignDetailKey(campaignId) });
      // Le périmètre / l'état conditionnent les valeurs proposées et le verrou des prix cibles (L10).
      void qc.invalidateQueries({ queryKey: targetPricesKey(campaignId) });
      void qc.invalidateQueries({ queryKey: campaignsListKey });
    },
  });
}
